import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  getBoard,
  moveTask,
  reorderTasks,
  type TaskView,
  type TaskStatus,
  type BoardData,
} from '../../api/backlog';
import { Plus } from 'lucide-react';
import BacklogCard from './components/BacklogCard';
import TaskModal from './components/TaskModal';
import PageHeader from '../../components/shared/PageHeader';
import './BacklogPage.css';

const COLUMNS: { status: TaskStatus; label: string }[] = [
  { status: 'TODO', label: 'To Do' },
  { status: 'IN_PROGRESS', label: 'In Progress' },
  { status: 'DONE', label: 'Done' },
  { status: 'CANCELLED', label: 'Cancelled' },
];

const BacklogPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskView | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);

  const { data: board, isLoading } = useQuery<BoardData>({
    queryKey: ['backlog-board'],
    queryFn: getBoard,
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, newStatus, newSortOrder }: { id: number; newStatus: TaskStatus; newSortOrder: number }) =>
      moveTask(id, { newStatus, newSortOrder }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backlog-board'] }),
  });

  const reorderMutation = useMutation({
    mutationFn: reorderTasks,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backlog-board'] }),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const emptyBoard: BoardData = { TODO: [], IN_PROGRESS: [], DONE: [], CANCELLED: [] };
  const allTasks: BoardData = board ?? emptyBoard;

  const findTaskById = (id: number): { task: TaskView; status: TaskStatus } | null => {
    for (const col of COLUMNS) {
      const tasks = allTasks[col.status] ?? [];
      const task = tasks.find((t) => t.id === id);
      if (task) return { task, status: col.status };
    }
    return null;
  };

  const findColumnForTask = (taskId: number): TaskStatus | null => {
    for (const col of COLUMNS) {
      const tasks = allTasks[col.status] ?? [];
      if (tasks.some((t) => t.id === taskId)) return col.status;
    }
    return null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !board) return;

    const activeTaskId = active.id as number;
    const overId = over.id;

    const sourceCol = findColumnForTask(activeTaskId);
    if (!sourceCol) return;

    // Determine target column: either the column of the over task, or overId is a column status
    let targetCol: TaskStatus;
    const overAsColumn = COLUMNS.find((c) => c.status === overId);
    if (overAsColumn) {
      targetCol = overAsColumn.status;
    } else {
      const overCol = findColumnForTask(overId as number);
      targetCol = overCol ?? sourceCol;
    }

    const sourceTasks = [...(board[sourceCol] ?? [])];
    const targetTasks = sourceCol === targetCol ? sourceTasks : [...(board[targetCol] ?? [])];

    const activeIdx = sourceTasks.findIndex((t) => t.id === activeTaskId);
    if (activeIdx === -1) return;

    if (sourceCol === targetCol) {
      // Reorder within same column
      const overIdx = sourceTasks.findIndex((t) => t.id === overId);
      if (overIdx === -1 || activeIdx === overIdx) return;

      const [moved] = sourceTasks.splice(activeIdx, 1);
      sourceTasks.splice(overIdx, 0, moved);

      // Optimistic update
      queryClient.setQueryData(['backlog-board'], {
        ...board,
        [sourceCol]: sourceTasks,
      });

      reorderMutation.mutate({
        status: sourceCol,
        taskIds: sourceTasks.map((t) => t.id),
      });
    } else {
      // Move between columns
      const [moved] = sourceTasks.splice(activeIdx, 1);
      const overIdx = targetTasks.findIndex((t) => t.id === overId);
      const insertIdx = overIdx >= 0 ? overIdx : targetTasks.length;
      moved.status = targetCol;
      targetTasks.splice(insertIdx, 0, moved);

      // Optimistic update
      queryClient.setQueryData(['backlog-board'], {
        ...board,
        [sourceCol]: sourceTasks,
        [targetCol]: targetTasks,
      });

      moveMutation.mutate({
        id: activeTaskId,
        newStatus: targetCol,
        newSortOrder: insertIdx,
      });

      // Reorder target column
      reorderMutation.mutate({
        status: targetCol,
        taskIds: targetTasks.map((t) => t.id),
      });
    }
  };

  const handleCardClick = (task: TaskView) => {
    if (activeId) return; // Don't open modal during drag
    setEditingTask(task);
    setModalOpen(true);
  };

  const handleNewTask = () => {
    setEditingTask(null);
    setModalOpen(true);
  };

  const activeTask = activeId ? findTaskById(activeId)?.task : null;

  const totalTasks = Object.values(allTasks).reduce((s, col) => s + (col?.length ?? 0), 0);

  return (
    <>
      <PageHeader
        title="Backlog"
        subtitle={isLoading ? 'Loading board…' : <><span className="hf-mono">{totalTasks}</span> tasks total</>}
        actions={
          <button className="hf-btn primary" onClick={handleNewTask}>
            <Plus size={14} /> New task
          </button>
        }
      />

      <div className="hf-content">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="backlog-board">
          {COLUMNS.map((col) => {
            const tasks = allTasks[col.status] ?? [];
            return (
              <div key={col.status} className={`backlog-column backlog-column--${col.status}`}>
                <div className="backlog-column-header">
                  <span>{col.label}</span>
                  <span className="count">{tasks.length}</span>
                </div>
                <SortableContext
                  items={tasks.map((t) => t.id)}
                  strategy={verticalListSortingStrategy}
                  id={col.status}
                >
                  <div className="backlog-column-body">
                    {tasks.map((task) => (
                      <BacklogCard key={task.id} task={task} onClick={handleCardClick} />
                    ))}
                  </div>
                </SortableContext>
              </div>
            );
          })}
        </div>

        <DragOverlay>
          {activeTask ? (
            <div className={`backlog-card backlog-card--${activeTask.priority}`} style={{ cursor: 'grabbing' }}>
              <div className="backlog-card-title">{activeTask.title}</div>
              <div className="backlog-card-meta">
                <span className={`backlog-priority-badge backlog-priority-badge--${activeTask.priority}`}>
                  {activeTask.priority}
                </span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <TaskModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        task={editingTask}
      />
      </div>
    </>
  );
};

export default BacklogPage;
